import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  ApiError,
  providerApi,
  setRoleElevationHandler,
  type ProviderElevationResponse,
  type RoleElevationRequest,
} from "../../lib/api";
import { useAppPreferences } from "../../lib/preferences";
import { membershipRoleLabel } from "./provider-workspace-helpers";

type PendingElevationRequest = RoleElevationRequest & {
  resolve: (grant: ProviderElevationResponse | null) => void;
};

/** Manager-approval prompt for role-restricted provider actions. Mounted once
 *  in the provider shell; while mounted it registers the global
 *  roleElevationHandler the request layer calls when a 403 carries an
 *  x-role-required header. Mirrors the useHardConfirm dialog pattern. */
export function useProviderElevation() {
  const { t } = useAppPreferences();
  const [pendingRequest, setPendingRequest] = useState<PendingElevationRequest | null>(null);
  const [approverEmail, setApproverEmail] = useState("");
  const [approverPassword, setApproverPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const emailInputRef = useRef<HTMLInputElement | null>(null);
  const pendingRequestRef = useRef<PendingElevationRequest | null>(null);
  const titleId = useId();
  const descriptionId = useId();

  const resetDialog = () => {
    pendingRequestRef.current = null;
    setPendingRequest(null);
    setApproverEmail("");
    setApproverPassword("");
    setSubmitting(false);
    setError(null);
  };

  const closeDialog = (grant: ProviderElevationResponse | null) => {
    const activeRequest = pendingRequestRef.current;
    if (!activeRequest) {
      return;
    }

    activeRequest.resolve(grant);
    resetDialog();
  };

  useEffect(() => {
    pendingRequestRef.current = pendingRequest;
  }, [pendingRequest]);

  // Register the request-layer elevation handler while the shell is mounted.
  useEffect(() => {
    setRoleElevationHandler(
      (request) =>
        new Promise<ProviderElevationResponse | null>((resolve) => {
          // A newer prompt supersedes a pending one, matching useHardConfirm.
          pendingRequestRef.current?.resolve(null);
          const nextRequest = { ...request, resolve };
          pendingRequestRef.current = nextRequest;
          setPendingRequest(nextRequest);
        }),
    );

    return () => {
      setRoleElevationHandler(null);
      pendingRequestRef.current?.resolve(null);
      pendingRequestRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!pendingRequest) {
      return;
    }

    emailInputRef.current?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeDialog(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [pendingRequest]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const activeRequest = pendingRequestRef.current;
    if (!activeRequest || submitting) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const grant = await providerApi.requestElevation({
        approver_email: approverEmail.trim(),
        approver_password: approverPassword,
      });
      activeRequest.resolve(grant);
      resetDialog();
    } catch (requestError) {
      setError(
        requestError instanceof ApiError
          ? requestError.detail
          : t("Could not verify approver credentials."),
      );
      setSubmitting(false);
    }
  };

  const dialogContent = pendingRequest ? (
    <div
      aria-hidden={false}
      className="ops-dialog-backdrop"
      onClick={() => closeDialog(null)}
      role="presentation"
    >
      <form
        aria-describedby={descriptionId}
        aria-labelledby={titleId}
        aria-modal="true"
        className="ops-dialog"
        onClick={(event) => event.stopPropagation()}
        onSubmit={(event) => void handleSubmit(event)}
        role="dialog"
      >
        <div className="ops-dialog-header">
          <p className="ops-kicker">{t("Elevation")}</p>
          <h2 className="ops-dialog-title" id={titleId}>
            {t("Manager approval required")}
          </h2>
          <p className="ops-copy" id={descriptionId}>
            {t(
              "This action needs {role} approval. Ask a manager or owner to enter their credentials — approved actions stay unlocked for 10 minutes.",
              { role: membershipRoleLabel(pendingRequest.roleRequired, t) },
            )}
          </p>
        </div>

        <div className="ops-dialog-body">
          {error ? (
            <div className="ops-banner" data-tone="danger" role="alert">
              {error}
            </div>
          ) : null}

          <label className="ops-field">
            <span className="ops-label">{t("Approver email")}</span>
            <input
              autoComplete="username"
              className="ops-input"
              disabled={submitting}
              onChange={(event) => setApproverEmail(event.target.value)}
              placeholder="manager@gaamed.com"
              ref={emailInputRef}
              required
              type="email"
              value={approverEmail}
            />
          </label>

          <label className="ops-field">
            <span className="ops-label">{t("Approver password")}</span>
            <input
              autoComplete="current-password"
              className="ops-input"
              disabled={submitting}
              onChange={(event) => setApproverPassword(event.target.value)}
              required
              type="password"
              value={approverPassword}
            />
          </label>
        </div>

        <div className="ops-dialog-actions">
          <button
            className="ops-button"
            data-variant="secondary"
            disabled={submitting}
            onClick={() => closeDialog(null)}
            type="button"
          >
            {t("Cancel")}
          </button>
          <button
            className="ops-button"
            data-variant="primary"
            disabled={submitting}
            type="submit"
          >
            {submitting ? t("Approving...") : t("Approve action")}
          </button>
        </div>
      </form>
    </div>
  ) : null;

  const dialog = dialogContent && typeof document !== "undefined"
    ? createPortal(dialogContent, document.body)
    : dialogContent;

  return { dialog };
}
